// src/pages/VoucherGame/VoucherGame.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Button,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Add,
  Refresh,
  LocalShipping,
  Inventory,
  Block,
} from "@mui/icons-material";

import useLayoutStore from "../../store/useLayoutStore";
import CustomTable from "../../components/common/CustomTable";
import CustomPaging from "../../components/common/CustomPaging";
import CustomTabs from "../../components/common/CustomTabs";

import {
  vgListBatches,
  vgCreateBatch,
  vgImportCodes,
  vgListVouchers,
  vgUpdateVoucherStatus,
  vgListPrizes,
  vgCreatePrize,
  vgGetRules,
  vgUpsertRules,
  vgListRedemptions,
} from "../../api/voucherGameApi";

const drawerWidth = 240;
const collapsedWidth = 80;

const toLocalInputValue = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(
    dt.getHours(),
  )}:${pad(dt.getMinutes())}`;
};

export default function VoucherGame() {
  const { isSidebarOpen } = useLayoutStore();

  const [tab, setTab] = useState(0);
  const tabLabels = [
    "Đợt phát hành",
    "Nhập mã in",
    "Danh sách voucher",
    "Danh mục giải",
    "Luật trúng thưởng",
    "Lịch sử đổi thưởng",
  ];

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const showToast = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // Shared reference data
  const [batches, setBatches] = useState([]);
  const [prizes, setPrizes] = useState([]);

  // Paging states
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ---------- Batches ----------
  const [batchDialog, setBatchDialog] = useState(false);
  const [batchForm, setBatchForm] = useState({
    batchCode: "",
    name: "",
    description: "",
    allowSpin: true,
    allowGiftbox: true,
    startsAt: "",
    endsAt: "",
    isActive: true,
    uiConfig: "",
  });
  const [batchList, setBatchList] = useState([]);
  const [batchTotal, setBatchTotal] = useState(0);

  // ---------- Import ----------
  const [importBatchId, setImportBatchId] = useState("");
  const [codesText, setCodesText] = useState("");
  const [importExpiresAt, setImportExpiresAt] = useState("");
  const [importNotes, setImportNotes] = useState("");

  // ---------- Vouchers ----------
  const [voucherFilter, setVoucherFilter] = useState({
    batchId: "",
    status: "",
    code: "",
  });
  const [voucherList, setVoucherList] = useState([]);
  const [voucherTotal, setVoucherTotal] = useState(0);

  // ---------- Prizes ----------
  const [prizeDialog, setPrizeDialog] = useState(false);
  const [prizeForm, setPrizeForm] = useState({
    prizeCode: "",
    name: "",
    description: "",
    prizeTypeCode: "COUPON",
    valueJson: '{"discountType":"PERCENT","value":10}',
    stockTotal: "",
    stockRemaining: "",
    isActive: true,
  });

  // ---------- Rules ----------
  const [ruleBatchId, setRuleBatchId] = useState("");
  const [ruleExperience, setRuleExperience] = useState("SPIN");
  const [rules, setRules] = useState([]);
  const [ruleLoading, setRuleLoading] = useState(false);

  // ---------- Redemptions ----------
  const [redFilter, setRedFilter] = useState({ batchId: "", status: "" });
  const [redList, setRedList] = useState([]);
  const [redTotal, setRedTotal] = useState(0);

  // -------------------- Load reference data --------------------
  const refreshBatches = async () => {
    const data = await vgListBatches({ page: 1, pageSize: 200 });
    const items = data.items ?? data; // tolerate backend shape
    setBatches(items || []);
  };

  const refreshPrizes = async () => {
    const data = await vgListPrizes({});
    const items = Array.isArray(data) ? data : (data.items ?? []);
    setPrizes(items || []);
  };

  useEffect(() => {
    (async () => {
      try {
        await refreshBatches();
        await refreshPrizes();
      } catch (e) {
        showToast(e?.message || "Tải dữ liệu tham chiếu thất bại", "error");
      }
    })();
    // eslint-disable-next-line
  }, []);

  // Reset paging when switching tabs
  useEffect(() => {
    setPage(1);
    setItemsPerPage(10);
  }, [tab]);
  useEffect(() => {
    if (tab === 4 && ruleBatchId) loadRules();
    // eslint-disable-next-line
  }, [tab, ruleBatchId, ruleExperience]);

  // -------------------- Tab loaders --------------------
  const loadBatchesTab = async () => {
    setLoading(true);
    try {
      const data = await vgListBatches({ page, pageSize: itemsPerPage });
      const items = data.items ?? data;
      setBatchList(items || []);
      setBatchTotal(data.total ?? (Array.isArray(items) ? items.length : 0));
    } catch (e) {
      showToast(e?.message || "Tải danh sách đợt phát hành thất bại", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadVouchersTab = async () => {
    setLoading(true);
    try {
      const data = await vgListVouchers({
        batchId: voucherFilter.batchId || undefined,
        status: voucherFilter.status || undefined,
        code: voucherFilter.code || undefined,
        page,
        pageSize: itemsPerPage,
      });
      const items = data.items ?? data;
      setVoucherList(items || []);
      setVoucherTotal(data.total ?? (Array.isArray(items) ? items.length : 0));
    } catch (e) {
      showToast(e?.message || "Tải danh sách voucher thất bại", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadRedemptionsTab = async () => {
    setLoading(true);
    try {
      const data = await vgListRedemptions({
        batchId: redFilter.batchId || undefined,
        status: redFilter.status || undefined,
        page,
        pageSize: itemsPerPage,
      });
      const items = data.items ?? data;
      setRedList(items || []);
      setRedTotal(data.total ?? (Array.isArray(items) ? items.length : 0));
    } catch (e) {
      showToast(e?.message || "Tải lịch sử đổi thưởng thất bại", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === 0) loadBatchesTab();
    if (tab === 2) loadVouchersTab();
    if (tab === 5) loadRedemptionsTab();
    // eslint-disable-next-line
  }, [tab, page, itemsPerPage]);

  // Reload vouchers when filter changes
  useEffect(() => {
    if (tab === 2) {
      setPage(1);
      loadVouchersTab();
    }
    // eslint-disable-next-line
  }, [voucherFilter.batchId, voucherFilter.status, voucherFilter.code]);

  // Reload redemptions when filter changes
  useEffect(() => {
    if (tab === 5) {
      setPage(1);
      loadRedemptionsTab();
    }
    // eslint-disable-next-line
  }, [redFilter.batchId, redFilter.status]);

  // -------------------- UI helpers --------------------
  const batchOptions = useMemo(() => batches || [], [batches]);

  const batchColumns = [
    { field: "batchId", label: "ID", minWidth: "80px" },
    { field: "batchCode", label: "Mã đợt", minWidth: "160px" },
    { field: "name", label: "Tên", minWidth: "220px" },
    {
      field: "isActive",
      label: "Kích hoạt",
      minWidth: "110px",
      customRender: (row) => (row.isActive ? "CÓ" : "KHÔNG"),
    },
    {
      field: "startsAt",
      label: "Bắt đầu",
      minWidth: "170px",
      customRender: (row) =>
        row.startsAt ? new Date(row.startsAt).toLocaleString("vi-VN") : "",
    },
    {
      field: "endsAt",
      label: "Kết thúc",
      minWidth: "170px",
      customRender: (row) =>
        row.endsAt ? new Date(row.endsAt).toLocaleString("vi-VN") : "",
    },
    {
      field: "allowSpin",
      label: "Cho quay",
      minWidth: "110px",
      customRender: (row) => (row.allowSpin ? "CÓ" : "KHÔNG"),
    },
    {
      field: "allowGiftbox",
      label: "Hộp quà",
      minWidth: "120px",
      customRender: (row) => (row.allowGiftbox ? "CÓ" : "KHÔNG"),
    },
  ];

  const voucherColumns = [
    { field: "voucherId", label: "ID", minWidth: "90px" },
    { field: "code", label: "Mã", minWidth: "180px" },
    { field: "status", label: "Trạng thái", minWidth: "130px" },
    { field: "batchCode", label: "Đợt", minWidth: "140px" },
    {
      field: "expiresAt",
      label: "Hết hạn",
      minWidth: "170px",
      customRender: (row) =>
        row.expiresAt ? new Date(row.expiresAt).toLocaleString("vi-VN") : "",
    },
    {
      field: "redeemedAt",
      label: "Đổi lúc",
      minWidth: "170px",
      customRender: (row) =>
        row.redeemedAt ? new Date(row.redeemedAt).toLocaleString("vi-VN") : "",
    },
    { field: "redeemedByUserId", label: "User ID", minWidth: "110px" },
    {
      field: "actions",
      label: "Thao tác",
      minWidth: "200px",
      customRender: (row) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            title="ĐÃ ĐÓNG GÓI"
            onClick={async () => {
              try {
                await vgUpdateVoucherStatus(row.code, { status: "PACKED" });
                showToast("Cập nhật: ĐÃ ĐÓNG GÓI");
                loadVouchersTab();
              } catch (e) {
                showToast(e?.message || "Cập nhật thất bại", "error");
              }
            }}
          >
            <Inventory fontSize="small" />
          </IconButton>

          <IconButton
            title="ĐÃ GỬI"
            onClick={async () => {
              try {
                await vgUpdateVoucherStatus(row.code, { status: "SHIPPED" });
                showToast("Cập nhật: ĐÃ GỬI");
                loadVouchersTab();
              } catch (e) {
                showToast(e?.message || "Cập nhật thất bại", "error");
              }
            }}
          >
            <LocalShipping fontSize="small" />
          </IconButton>

          <IconButton
            title="HUỶ MÃ"
            color="error"
            onClick={async () => {
              if (!window.confirm("Bạn chắc chắn muốn huỷ mã này?")) return;
              try {
                await vgUpdateVoucherStatus(row.code, { status: "VOID" });
                showToast("Cập nhật: ĐÃ HUỶ");
                loadVouchersTab();
              } catch (e) {
                showToast(e?.message || "Cập nhật thất bại", "error");
              }
            }}
          >
            <Block fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const prizeColumns = [
    { field: "prizeId", label: "ID", minWidth: "80px" },
    { field: "prizeCode", label: "Mã giải", minWidth: "160px" },
    { field: "name", label: "Tên giải", minWidth: "220px" },
    { field: "type", label: "Loại", minWidth: "120px" },
    { field: "stockTotal", label: "Tổng tồn", minWidth: "120px" },
    { field: "stockRemaining", label: "Còn lại", minWidth: "120px" },
    {
      field: "isActive",
      label: "Kích hoạt",
      minWidth: "110px",
      customRender: (row) => (row.isActive ? "CÓ" : "KHÔNG"),
    },
  ];

  const redColumns = [
    { field: "redemptionId", label: "ID", minWidth: "90px" },
    { field: "status", label: "Trạng thái", minWidth: "120px" },
    { field: "failureReason", label: "Lý do", minWidth: "200px" },
    { field: "userId", label: "User ID", minWidth: "110px" },
    { field: "batchCode", label: "Đợt", minWidth: "140px" },
    { field: "code", label: "Mã", minWidth: "180px" },
    {
      field: "prize",
      label: "Giải",
      minWidth: "260px",
      customRender: (row) =>
        row.prize ? `${row.prize.prizeCode} - ${row.prize.name}` : "",
    },
    {
      field: "createdAt",
      label: "Tạo lúc",
      minWidth: "180px",
      customRender: (row) =>
        row.createdAt ? new Date(row.createdAt).toLocaleString("vi-VN") : "",
    },
  ];

  // -------------------- Actions --------------------
  const handleCreateBatch = async () => {
    try {
      const payload = {
        batchCode: batchForm.batchCode.trim(),
        name: batchForm.name.trim(),
        description: batchForm.description || null,
        allowSpin: !!batchForm.allowSpin,
        allowGiftbox: !!batchForm.allowGiftbox,
        startsAt: batchForm.startsAt
          ? new Date(batchForm.startsAt).toISOString()
          : null,
        endsAt: batchForm.endsAt
          ? new Date(batchForm.endsAt).toISOString()
          : null,
        isActive: !!batchForm.isActive,
        uiConfig: batchForm.uiConfig ? JSON.parse(batchForm.uiConfig) : null,
      };

      await vgCreateBatch(payload);
      showToast("Tạo đợt phát hành thành công");
      setBatchDialog(false);
      setBatchForm({
        batchCode: "",
        name: "",
        description: "",
        allowSpin: true,
        allowGiftbox: true,
        startsAt: "",
        endsAt: "",
        isActive: true,
        uiConfig: "",
      });
      await refreshBatches();
      await loadBatchesTab();
    } catch (e) {
      showToast(
        e?.message || "Tạo đợt thất bại (kiểm tra uiConfig JSON)",
        "error",
      );
    }
  };

  const handleImport = async () => {
    if (!importBatchId)
      return showToast("Vui lòng chọn đợt phát hành trước", "error");
    if (!codesText.trim())
      return showToast("Vui lòng nhập danh sách mã", "error");

    try {
      setLoading(true);
      const payload = {
        batchId: Number(importBatchId),
        codesText,
        expiresAt: importExpiresAt
          ? new Date(importExpiresAt).toISOString()
          : null,
        notes: importNotes || null,
      };
      const res = await vgImportCodes(payload);
      showToast(
        `Nhập mã thành công. Thêm mới: ${res.inserted ?? 0}, Trùng: ${res.duplicated ?? 0}`,
      );
      setCodesText("");
      setImportNotes("");
    } catch (e) {
      showToast(e?.message || "Nhập mã thất bại", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadRules = async () => {
    if (!ruleBatchId) return showToast("Vui lòng chọn đợt", "error");
    setRuleLoading(true);
    try {
      const data = await vgGetRules(Number(ruleBatchId), ruleExperience);
      setRules(Array.isArray(data) ? data : (data.items ?? []));
    } catch (e) {
      showToast(e?.message || "Tải luật thất bại", "error");
    } finally {
      setRuleLoading(false);
    }
  };

  const saveRules = async () => {
    if (!ruleBatchId) return showToast("Vui lòng chọn đợt", "error");
    try {
      const payload = {
        experience: ruleExperience,
        rules: rules.map((r) => ({
          prizeId: Number(r.prizeId),
          weight: Number(r.weight || 0),
          dailyLimit:
            r.dailyLimit === "" ||
            r.dailyLimit === null ||
            r.dailyLimit === undefined
              ? null
              : Number(r.dailyLimit),
          totalLimit:
            r.totalLimit === "" ||
            r.totalLimit === null ||
            r.totalLimit === undefined
              ? null
              : Number(r.totalLimit),
          isActive: !!r.isActive,
        })),
      };
      await vgUpsertRules(Number(ruleBatchId), payload);
      showToast("Lưu luật thành công");
      await loadRules();
    } catch (e) {
      showToast(e?.message || "Lưu luật thất bại", "error");
    }
  };

  // -------------------- Render --------------------
  return (
    <Box sx={{ flexGrow: 1, p: 2, width: "100%", minWidth: 0 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          Quản trị Voucher Game
        </Typography>
        <Button
          startIcon={<Refresh />}
          variant="outlined"
          onClick={async () => {
            try {
              await refreshBatches();
              await refreshPrizes();
              showToast("Đã làm mới dữ liệu");
            } catch (e) {
              showToast("Làm mới thất bại", "error");
            }
          }}
        >
          Làm mới
        </Button>
      </Box>

      <CustomTabs activeTab={tab} setActiveTab={setTab} tabLabels={tabLabels} />

      {/* TAB 0: Batches */}
      {tab === 0 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Đợt phát hành</Typography>
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => setBatchDialog(true)}
            >
              Tạo đợt
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <CustomTable columns={batchColumns} data={batchList} />
              </Box>

              <CustomPaging
                page={page}
                setPage={setPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                totalItems={batchTotal}
              />
            </>
          )}

          <Dialog
            open={batchDialog}
            onClose={() => setBatchDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Tạo đợt phát hành</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: "1fr 1fr",
                  mt: 1,
                }}
              >
                <TextField
                  label="Mã đợt"
                  value={batchForm.batchCode}
                  onChange={(e) =>
                    setBatchForm((p) => ({ ...p, batchCode: e.target.value }))
                  }
                />
                <TextField
                  label="Tên đợt"
                  value={batchForm.name}
                  onChange={(e) =>
                    setBatchForm((p) => ({ ...p, name: e.target.value }))
                  }
                />
                <TextField
                  label="Mô tả"
                  value={batchForm.description}
                  onChange={(e) =>
                    setBatchForm((p) => ({ ...p, description: e.target.value }))
                  }
                />
                <TextField
                  label="Cấu hình UI (JSON)"
                  value={batchForm.uiConfig}
                  onChange={(e) =>
                    setBatchForm((p) => ({ ...p, uiConfig: e.target.value }))
                  }
                />
                <TextField
                  type="datetime-local"
                  label="Bắt đầu"
                  InputLabelProps={{ shrink: true }}
                  value={batchForm.startsAt}
                  onChange={(e) =>
                    setBatchForm((p) => ({ ...p, startsAt: e.target.value }))
                  }
                />
                <TextField
                  type="datetime-local"
                  label="Kết thúc"
                  InputLabelProps={{ shrink: true }}
                  value={batchForm.endsAt}
                  onChange={(e) =>
                    setBatchForm((p) => ({ ...p, endsAt: e.target.value }))
                  }
                />
              </Box>

              <Box
                sx={{ display: "flex", gap: 3, mt: 2, alignItems: "center" }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Switch
                    checked={batchForm.isActive}
                    onChange={(e) =>
                      setBatchForm((p) => ({
                        ...p,
                        isActive: e.target.checked,
                      }))
                    }
                  />
                  <Typography>Kích hoạt</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Switch
                    checked={batchForm.allowSpin}
                    onChange={(e) =>
                      setBatchForm((p) => ({
                        ...p,
                        allowSpin: e.target.checked,
                      }))
                    }
                  />
                  <Typography>Cho phép quay (SPIN)</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Switch
                    checked={batchForm.allowGiftbox}
                    onChange={(e) =>
                      setBatchForm((p) => ({
                        ...p,
                        allowGiftbox: e.target.checked,
                      }))
                    }
                  />
                  <Typography>Cho phép hộp quà (GIFTBOX)</Typography>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setBatchDialog(false)}>Huỷ</Button>
              <Button variant="contained" onClick={handleCreateBatch}>
                Tạo
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* TAB 1: Import */}
      {tab === 1 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Nhập danh sách mã đã in
          </Typography>

          <Box
            sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr 1fr" }}
          >
            <FormControl>
              <InputLabel>Đợt phát hành</InputLabel>
              <Select
                label="Đợt phát hành"
                value={importBatchId}
                onChange={(e) => setImportBatchId(e.target.value)}
              >
                {batchOptions.map((b) => (
                  <MenuItem key={b.batchId} value={b.batchId}>
                    {b.batchCode} - {b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              type="datetime-local"
              label="Hết hạn (tuỳ chọn)"
              InputLabelProps={{ shrink: true }}
              value={importExpiresAt}
              onChange={(e) => setImportExpiresAt(e.target.value)}
            />

            <TextField
              label="Ghi chú (tuỳ chọn)"
              value={importNotes}
              onChange={(e) => setImportNotes(e.target.value)}
            />
          </Box>

          <TextField
            label="Danh sách mã (mỗi dòng 1 mã)"
            multiline
            minRows={10}
            sx={{ mt: 2, width: "100%" }}
            value={codesText}
            onChange={(e) => setCodesText(e.target.value)}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={loading}
            >
              Nhập mã
            </Button>
          </Box>

          {loading && (
            <Box sx={{ textAlign: "center", mt: 3 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      )}

      {/* TAB 2: Vouchers */}
      {tab === 2 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Danh sách voucher đã in
          </Typography>

          <Box
            sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr 1fr" }}
          >
            <FormControl>
              <InputLabel>Đợt</InputLabel>
              <Select
                label="Đợt"
                value={voucherFilter.batchId}
                onChange={(e) =>
                  setVoucherFilter((p) => ({ ...p, batchId: e.target.value }))
                }
              >
                <MenuItem value="">Tất cả</MenuItem>
                {batchOptions.map((b) => (
                  <MenuItem key={b.batchId} value={b.batchId}>
                    {b.batchCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel>Trạng thái</InputLabel>
              <Select
                label="Trạng thái"
                value={voucherFilter.status}
                onChange={(e) =>
                  setVoucherFilter((p) => ({ ...p, status: e.target.value }))
                }
              >
                <MenuItem value="">Tất cả</MenuItem>
                {["NEW", "PACKED", "SHIPPED", "REDEEMED", "VOID"].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Tìm theo mã (chứa...)"
              value={voucherFilter.code}
              onChange={(e) =>
                setVoucherFilter((p) => ({ ...p, code: e.target.value }))
              }
            />
          </Box>

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <CustomTable columns={voucherColumns} data={voucherList} />
              <CustomPaging
                page={page}
                setPage={setPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                totalItems={voucherTotal}
              />
            </>
          )}
        </Box>
      )}

      {/* TAB 3: Prizes */}
      {tab === 3 && (
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Danh mục giải thưởng</Typography>
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => setPrizeDialog(true)}
            >
              Tạo giải
            </Button>
          </Box>

          <CustomTable
            columns={prizeColumns}
            data={(prizes || []).map((p) => ({
              ...p,
              type: p.type || p.prizeType?.code || p.prizeTypeCode || "",
            }))}
          />

          <Dialog
            open={prizeDialog}
            onClose={() => setPrizeDialog(false)}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Tạo giải thưởng</DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: "1fr 1fr",
                  mt: 1,
                }}
              >
                <TextField
                  label="Mã giải"
                  value={prizeForm.prizeCode}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, prizeCode: e.target.value }))
                  }
                />
                <TextField
                  label="Tên giải"
                  value={prizeForm.name}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, name: e.target.value }))
                  }
                />
                <TextField
                  label="Mô tả"
                  value={prizeForm.description}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, description: e.target.value }))
                  }
                />
                <FormControl>
                  <InputLabel>Loại giải</InputLabel>
                  <Select
                    label="Loại giải"
                    value={prizeForm.prizeTypeCode}
                    onChange={(e) =>
                      setPrizeForm((p) => ({
                        ...p,
                        prizeTypeCode: e.target.value,
                      }))
                    }
                  >
                    {["COUPON", "POINT", "FREESHIP", "PRODUCT", "OTHER"].map(
                      (t) => (
                        <MenuItem key={t} value={t}>
                          {t}
                        </MenuItem>
                      ),
                    )}
                  </Select>
                </FormControl>

                <TextField
                  label="Tổng tồn (tuỳ chọn)"
                  value={prizeForm.stockTotal}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, stockTotal: e.target.value }))
                  }
                />
                <TextField
                  label="Còn lại (tuỳ chọn)"
                  value={prizeForm.stockRemaining}
                  onChange={(e) =>
                    setPrizeForm((p) => ({
                      ...p,
                      stockRemaining: e.target.value,
                    }))
                  }
                />

                <TextField
                  label="ValueJson"
                  value={prizeForm.valueJson}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, valueJson: e.target.value }))
                  }
                  multiline
                  minRows={4}
                  sx={{ gridColumn: "1 / span 2" }}
                />
              </Box>

              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}
              >
                <Switch
                  checked={prizeForm.isActive}
                  onChange={(e) =>
                    setPrizeForm((p) => ({ ...p, isActive: e.target.checked }))
                  }
                />
                <Typography>Kích hoạt</Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPrizeDialog(false)}>Huỷ</Button>
              <Button
                variant="contained"
                onClick={async () => {
                  try {
                    const payload = {
                      prizeCode: prizeForm.prizeCode.trim(),
                      name: prizeForm.name.trim(),
                      description: prizeForm.description || null,
                      prizeTypeCode: prizeForm.prizeTypeCode,
                      valueJson: prizeForm.valueJson,
                      stockTotal:
                        prizeForm.stockTotal === ""
                          ? null
                          : Number(prizeForm.stockTotal),
                      stockRemaining:
                        prizeForm.stockRemaining === ""
                          ? null
                          : Number(prizeForm.stockRemaining),
                      isActive: !!prizeForm.isActive,
                    };
                    await vgCreatePrize(payload);
                    showToast("Tạo giải thành công");
                    setPrizeDialog(false);
                    await refreshPrizes();
                  } catch (e) {
                    showToast(
                      e?.message || "Tạo giải thất bại (kiểm tra ValueJson)",
                      "error",
                    );
                  }
                }}
              >
                Tạo
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      )}

      {/* TAB 4: Rules */}
      {tab === 4 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Thiết lập luật trúng thưởng
          </Typography>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: "1fr 1fr 1fr",
              alignItems: "center",
            }}
          >
            <FormControl>
              <InputLabel>Đợt</InputLabel>
              <Select
                label="Đợt"
                value={ruleBatchId}
                onChange={(e) => setRuleBatchId(e.target.value)}
              >
                <MenuItem value="">Chọn</MenuItem>
                {batchOptions.map((b) => (
                  <MenuItem key={b.batchId} value={b.batchId}>
                    {b.batchCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel>Trải nghiệm</InputLabel>
              <Select
                label="Trải nghiệm"
                value={ruleExperience}
                onChange={(e) => setRuleExperience(e.target.value)}
              >
                <MenuItem value="SPIN">QUAY (SPIN)</MenuItem>
                <MenuItem value="GIFTBOX">HỘP QUÀ (GIFTBOX)</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={loadRules}
                disabled={ruleLoading}
              >
                Tải
              </Button>
              <Button
                variant="contained"
                onClick={saveRules}
                disabled={ruleLoading}
              >
                Lưu
              </Button>
              <Button
                variant="text"
                onClick={() =>
                  setRules((prev) => [
                    ...prev,
                    {
                      prizeId: prizes?.[0]?.prizeId || "",
                      weight: 100,
                      dailyLimit: "",
                      totalLimit: "",
                      isActive: true,
                    },
                  ])
                }
              >
                + Thêm luật
              </Button>
            </Box>
          </Box>

          {ruleLoading ? (
            <Box sx={{ textAlign: "center", mt: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {/* Simple editable table */}
              <Box sx={{ display: "grid", gap: 1 }}>
                {rules.map((r, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "grid",
                      gap: 1,
                      gridTemplateColumns: "2fr 1fr 1fr 1fr 0.9fr 0.7fr",
                      alignItems: "center",
                    }}
                  >
                    <FormControl size="small">
                      <Select
                        value={r.prizeId ?? ""}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((x, i) =>
                              i === idx ? { ...x, prizeId: e.target.value } : x,
                            ),
                          )
                        }
                      >
                        {(prizes || []).map((p) => (
                          <MenuItem key={p.prizeId} value={p.prizeId}>
                            {p.prizeCode} - {p.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <TextField
                      size="small"
                      label="Tỷ trọng"
                      value={r.weight ?? 0}
                      onChange={(e) =>
                        setRules((prev) =>
                          prev.map((x, i) =>
                            i === idx ? { ...x, weight: e.target.value } : x,
                          ),
                        )
                      }
                    />

                    <TextField
                      size="small"
                      label="Giới hạn/ngày"
                      value={r.dailyLimit ?? ""}
                      onChange={(e) =>
                        setRules((prev) =>
                          prev.map((x, i) =>
                            i === idx
                              ? { ...x, dailyLimit: e.target.value }
                              : x,
                          ),
                        )
                      }
                    />

                    <TextField
                      size="small"
                      label="Giới hạn tổng"
                      value={r.totalLimit ?? ""}
                      onChange={(e) =>
                        setRules((prev) =>
                          prev.map((x, i) =>
                            i === idx
                              ? { ...x, totalLimit: e.target.value }
                              : x,
                          ),
                        )
                      }
                    />

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Switch
                        checked={!!r.isActive}
                        onChange={(e) =>
                          setRules((prev) =>
                            prev.map((x, i) =>
                              i === idx
                                ? { ...x, isActive: e.target.checked }
                                : x,
                            ),
                          )
                        }
                      />
                      <Typography variant="body2">Kích hoạt</Typography>
                    </Box>

                    <Button
                      color="error"
                      onClick={() =>
                        setRules((prev) => prev.filter((_, i) => i !== idx))
                      }
                    >
                      Xoá
                    </Button>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* TAB 5: Redemptions */}
      {tab === 5 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Lịch sử đổi thưởng
          </Typography>

          <Box
            sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr 1fr" }}
          >
            <FormControl>
              <InputLabel>Đợt</InputLabel>
              <Select
                label="Đợt"
                value={redFilter.batchId}
                onChange={(e) =>
                  setRedFilter((p) => ({ ...p, batchId: e.target.value }))
                }
              >
                <MenuItem value="">Tất cả</MenuItem>
                {batchOptions.map((b) => (
                  <MenuItem key={b.batchId} value={b.batchId}>
                    {b.batchCode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <InputLabel>Trạng thái</InputLabel>
              <Select
                label="Trạng thái"
                value={redFilter.status}
                onChange={(e) =>
                  setRedFilter((p) => ({ ...p, status: e.target.value }))
                }
              >
                <MenuItem value="">Tất cả</MenuItem>
                <MenuItem value="SUCCESS">THÀNH CÔNG</MenuItem>
                <MenuItem value="FAILED">THẤT BẠI</MenuItem>
              </Select>
            </FormControl>

            <Box />
          </Box>

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <CustomTable columns={redColumns} data={redList} />
              <CustomPaging
                page={page}
                setPage={setPage}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                totalItems={redTotal}
              />
            </>
          )}
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
